import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * SlotPopup - Anchored to the ghost block, rendered via portal.
 * Defaults to below the ghost. Clamped to stay fully within the viewport.
 */
function SlotPopup({ ghostRef, onAction, onClose }) {
  const popupRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!popupRef.current) return;

    const popupRect = popupRef.current.getBoundingClientRect();
    const pad = 12;
    const isMobile = window.innerWidth < 768;

    if (isMobile || !ghostRef.current) {
      // Center on screen for mobile or when ghost is unavailable
      setPos({
        left: (window.innerWidth - popupRect.width) / 2,
        top: (window.innerHeight - popupRect.height) / 2,
      });
      return;
    }

    const ghostRect = ghostRef.current.getBoundingClientRect();

    // Center horizontally on the ghost
    let left = ghostRect.left + ghostRect.width / 2 - popupRect.width / 2;
    // Default: position below the ghost
    let top = ghostRect.bottom + 8;

    // If below goes off screen, try above
    if (top + popupRect.height > window.innerHeight - pad) {
      top = ghostRect.top - popupRect.height - 8;
    }
    // Final clamp: ensure always within viewport
    if (top < pad) top = pad;
    if (top + popupRect.height > window.innerHeight - pad) {
      top = window.innerHeight - pad - popupRect.height;
    }
    if (left < pad) left = pad;
    if (left + popupRect.width > window.innerWidth - pad) {
      left = window.innerWidth - pad - popupRect.width;
    }

    setPos({ left, top });
  }, [ghostRef]);

  return ReactDOM.createPortal(
    <div className="dv2-slot-popup-overlay" onClick={onClose}>
      <div
        ref={popupRef}
        className="dv2-slot-popup"
        style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden', left: 0, top: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <button className="dv2-slot-popup-item" onClick={() => onAction('appointment')}>
          <span className="dv2-slot-popup-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M3 8H17" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M7 2V5M13 2V5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M10 11V14M8.5 12.5H11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </span>
          <span className="dv2-slot-popup-label">Booking</span>
        </button>
        <button className="dv2-slot-popup-item" onClick={() => onAction('event')}>
          <span className="dv2-slot-popup-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M10 6.5V10L12.5 11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </span>
          <span className="dv2-slot-popup-label">Event</span>
        </button>
        <button className="dv2-slot-popup-item" onClick={() => onAction('holiday')}>
          <span className="dv2-slot-popup-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="8" r="4" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M10 4V2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M14 8H16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M4 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M12.8 5.2L14.2 3.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M5.8 3.8L7.2 5.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M3 14H17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M5 17H15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </span>
          <span className="dv2-slot-popup-label">Leave</span>
        </button>
      </div>
    </div>,
    document.body
  );
}

export default React.memo(SlotPopup);
